// IAS login screen — replaces upstream's `metabase/auth/components/Login`.
// Hooked in at the single import site in `metabase/routes.tsx` so the entire
// SSO-only login UX stays in this overlay directory.
//
// Flow: user clicks "Access amelia" → we mark session storage so a mid-redirect
// reload doesn't leave the button spinning → amelia SDK owns the OIDC dance →
// on return, `AppSsoProvider.onSigninCallback` exchanges the token via
// `/api/oidc-auth` for a Metabase session cookie.

import { useSso } from "@intelisysaviation/amelia-sso-client-react";
import { Box, Flex } from "@mantine/core";
import { useEffect, useState } from "react";
import { t } from "ttag";

import { usePageTitle } from "metabase/hooks/use-page-title";
import { Button, Text } from "metabase/ui";

import S from "./IasLogin.module.css";
import {
  isLoggingIn,
  persistIsLoggingIn,
  removeIsLoggingIn,
} from "./app-utils";
import { getConfigSettings } from "./configs";

export const IasLogin = (): JSX.Element => {
  usePageTitle(t`Login`);

  const sso = useSso();
  const [loading, setLoading] = useState(isLoggingIn());
  const { appStage } = getConfigSettings();

  useEffect(() => {
    // If the user manually reloaded mid-redirect, clear the sticky spinner.
    if (window.performance.navigation.type === 1) {
      removeIsLoggingIn();
      setLoading(false);
    }
  }, []);

  const handleLogin = () => {
    persistIsLoggingIn();
    setLoading(true);
    sso.login();
  };

  return (
    <Box className={S.layoutRoot} data-testid="login-page">
      <Box className={S.layoutBody}>
        <Flex align="stretch" flex={1} gap="xs">
          <Box className={S.leftLayout}>
            <Box className={S.contentContainer}>
              <img
                src="/app/assets/img/main-logo.svg"
                alt="Insights"
                className={S.mainLogo}
              />

              <Box>
                <Text size="1.75rem" fw={700} c="text-brand-strong" lh="2.1rem">
                  {t`Welcome Back!`}
                </Text>
                <Text size="1rem" className={S.subheading}>
                  {t`To access you will need to sign in with your amelia account`}
                </Text>
              </Box>

              <Button
                className={S.loginButton}
                loading={loading}
                onClick={handleLogin}
                radius="xl"
                variant="filled"
                mt="1.5rem"
              >
                <Text c="white">{t`Access amelia`}</Text>
              </Button>
            </Box>
          </Box>

          <Box className={S.rightLayout} visibleFrom="md">
            <Box className={`${S.contentContainer} ${S.rightContent}`}>
              <Box className={S.brandTile} w={64} h={64}>
                <img
                  src="/app/assets/img/app-icon.svg"
                  alt="Insights"
                  className={S.brandTileImg}
                />
              </Box>
              <Box className={S.rightBlurb}>
                <Text size="1.75rem" fw={700} c="text-brand-strong" lh="1.5">
                  {t`Insights`}
                </Text>
                <Text size="1.25rem" c="text-brand-strong" lh="1.5rem">
                  {t`Discover and analyze operational, financial, and performance data through interactive dashboards`}
                </Text>
              </Box>
            </Box>
          </Box>
        </Flex>
      </Box>

      <Box className={S.layoutFooter}>
        <Box className={S.stageBadge}>
          <Text size="0.875rem" className={S.stageBadgeText}>
            {appStage || t`Production`}
          </Text>
        </Box>
        <img
          src="/app/assets/img/amelia/amelia-res.svg"
          alt="Amelia Logo"
          className={S.footerLogo}
        />
      </Box>
    </Box>
  );
};
